import { AlertCircle, Loader2 } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { useFetcher } from "~/hooks/useFetcher";
import { ToolInfo } from "~/services/agents";

interface ToolListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** Endpoint to fetch the tools from. Pass `null` to skip fetching (the modal
   * fetches lazily — only while open). */
  fetchPath: string | null;
}

export const ToolListDialog = ({
  open,
  onOpenChange,
  title,
  description,
  fetchPath,
}: ToolListDialogProps) => {
  const {
    data: tools,
    isLoading,
    error,
  } = useFetcher<ToolInfo[]>(open ? fetchPath : null, { revalidateOnFocus: false });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[80vh] gap-0 overflow-hidden"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <ToolListBody isLoading={isLoading} error={error} tools={tools} />
      </DialogContent>
    </Dialog>
  );
};

interface ToolListBodyProps {
  isLoading: boolean;
  error: unknown;
  tools: ToolInfo[] | undefined;
}

const ToolListBody = ({ isLoading, error, tools }: ToolListBodyProps) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        Loading tools…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-2 py-10 text-sm text-muted-foreground">
        <AlertCircle className="size-5 text-destructive" />
        Couldn’t load tools for this item.
      </div>
    );
  }

  if (!tools?.length) {
    return <p className="py-10 text-center text-sm text-muted-foreground">No tools available</p>;
  }

  return (
    <>
      <p className="pt-1 pb-2 text-xs text-muted-foreground">
        {tools.length} {tools.length === 1 ? "tool" : "tools"}
      </p>
      <Accordion type="multiple" className="-mt-1 max-h-[55vh] overflow-y-auto pr-1">
        {tools.map((tool) => (
          <AccordionItem key={tool.name} value={tool.name}>
            <AccordionTrigger className="py-3 font-mono text-sm">{tool.name}</AccordionTrigger>
            <AccordionContent className="flex flex-col gap-2 text-sm text-muted-foreground">
              {toParagraphs(tool.description).map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </>
  );
};

/** Split a description into paragraphs on blank lines. Within a paragraph the
 * browser collapses the docstring's hard-wrapped single newlines into spaces,
 * so each paragraph flows naturally instead of breaking mid-sentence. */
function toParagraphs(description?: string): string[] {
  const paragraphs = (description ?? "No description provided.")
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
  return paragraphs.length ? paragraphs : ["No description provided."];
}
